import FF from "@/components/forms/FormField.module.css";

import { EstimatedMonthlyCostRange } from "@/components/EstimatedMonthlyCostRange.tsx";
import { HeroBlock } from "@/components/HeroBlock.tsx";
import { Separator } from "@/components/Separator.tsx";
import { CreateFormAction } from "@/components/forms/CreateFormAction.tsx";
import { FormDebugger } from "@/components/forms/FormDebugger.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { MonthlyCostField } from "@/components/forms/MonthlyCostField.tsx";
import { RadioGroupDiv } from "@/components/forms/RadioGroupDiv.tsx";
import TF from "@/components/forms/TextField.module.css";
import { WHookForm } from "@/components/forms/WHookForm.tsx";
import { WTextField, type WTextFieldProps } from "@/components/forms/WTextField.tsx";
import { useAriaField } from "@/components/forms/ariaFieldRegister.ts";
import { CreateAnother, LoadingProgress } from "@/components/loading/Loading.tsx";
import { useConfig } from "@/config.ts";
import { getResourceDescription } from "@/modules/api/resourceTypes.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { BillingAccountField } from "@/modules/billing/forms/BillingAccountField.tsx";
import { type VmPriceFieldsOmitSize, VmSizeSelect } from "@/modules/compute/VmSizeSelect.tsx";
import { extractOsFields } from "@/modules/compute/os/os.ts";
import { usePackages } from "@/modules/compute/vmPackages.ts";
import { sizeToVmPriceFields, type SizeValue } from "@/modules/compute/vmSizeSelectUtils.ts";
import { LocationFieldCustom } from "@/modules/location/LocationField.tsx";
import { useLocationsForType } from "@/modules/location/locationQuery.ts";
import { CreateAndAssignIpField } from "@/modules/network/CreateAndAssignIpField.tsx";
import { VpcNetworkSelect } from "@/modules/network/VpcNetworkSelect.tsx";
import { canCreateResourceHostPoolsAtom } from "@/modules/pools/hostPoolsQuery.ts";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import {
    getKubernetesPrice,
    getServiceCreatePrice,
    type KubernetesServicePriceFields,
    type Price,
    type ResourcePrices,
    type VmPriceFields,
} from "@/modules/pricing/resourcePricing.ts";
import { FailoverNodeSelect } from "@/modules/services/FailoverNodeSelect.tsx";
import { MaintenanceFormContent } from "@/modules/services/kubernetes/MaintenanceEditModal.tsx";
import { NodePoolsList } from "@/modules/services/kubernetes/NodePoolsList.tsx";
import { getFormattedMaintenance } from "@/modules/services/kubernetes/maintenanceSpec.tsx";
import { serviceLink } from "@/modules/services/links.ts";
import { OsVersionSelectCustom } from "@/modules/services/os/OsVersionSelect.tsx";
import { type ServiceCreateInputs, ServiceCreateViewModel } from "@/modules/services/serviceCreateViewModel.ts";
import { allServiceImagesByNameAtom, availableServiceImagesAtom } from "@/modules/services/serviceImagesQuery.ts";
import { createServiceMutation, SERVICE_CREATE_TIMER } from "@/modules/services/servicesQuery.ts";
import { cn } from "@/utils/classNames.ts";
import { castSetter } from "@/utils/getField.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useOnce } from "@/utils/react/useOnce.ts";
import { useGenerateUuid } from "@/utils/useGenerateUuid.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { Navigate } from "@tanstack/react-router";
import type { KubernetesProviderWorker, ManagedServiceCreateBody } from "@warrenio/api-spec/spec.oats.gen";
import { filterObjNulls } from "@warrenio/utils/collections/filterNulls";
import { mustGet } from "@warrenio/utils/collections/mustGet";
import { useEffect, useMemo } from "react";
import { Input } from "react-aria-components";
import { Controller, type SubmitHandler, useForm, useFormContext } from "react-hook-form";

interface ServiceCreateProps {
    inputs?: Partial<ServiceCreateInputs>;
    previousRequestUuid?: string;
    onChangeInputs?: (inputs: ServiceCreateInputs) => void;
}

export function ServiceCreate(props: ServiceCreateProps) {
    return import.meta.env.DEV ? <ServiceCreateDebugger {...props} /> : <ServiceCreateForm {...props} />;
}

function ServiceCreateForm({ inputs, previousRequestUuid, onChangeInputs }: ServiceCreateProps) {
    //#region Hooks
    const { t } = useLingui();
    const locations = useLocationsForType("managed_service");
    const allImages = useSuspenseQueryAtom(allServiceImagesByNameAtom);
    const availableImages = useSuspenseQueryAtom(availableServiceImagesAtom);
    const allSizePackages = usePackages();

    const pools = useSuspenseQueryAtom(canCreateResourceHostPoolsAtom);

    const { privateNetworksEnabled } = useConfig();

    const mutation = useStandardMutation(createServiceMutation);

    const requestUuid = useGenerateUuid(previousRequestUuid);

    const viewModel = useOnce(() => {
        return new ServiceCreateViewModel(availableImages, locations, allSizePackages, pools, inputs);
    });

    const defaultValues = useMemo(() => viewModel.getDefaultValues(), [viewModel]);

    /// Form
    const form = useForm<ServiceCreateInputs>({
        disabled: mutation.isPending,

        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues,
    });

    const { watch, control, setValue, getValues } = form;

    viewModel.attachGetterAndSetter(getValues, castSetter(setValue));

    useEffect(() => {
        const subscription = watch(() => {
            onChangeInputs?.(getValues()); // for form debugger
        });
        return () => subscription.unsubscribe();
    }, [watch, getValues, onChangeInputs]);

    //#endregion Hooks

    if (mutation.isSuccess) {
        return <Navigate {...serviceLink(mutation.data)} />;
    }

    function getSelectedImage() {
        const serviceValue = watch("service");
        return mustGet(allImages, serviceValue.os_name, "Service OS");
    }

    function getMultiNodeSettings(): { available: boolean; description?: string } {
        const serviceImage = getSelectedImage();
        return {
            available: serviceImage.is_multi_node_available,
            description: serviceImage.multi_node_description,
        };
    }

    function isKubernetesSelected() {
        return getSelectedImage().service_name === "kubernetes";
    }

    function priceCalculator(prices: ResourcePrices, service: VmPriceFields): Price {
        const {
            // Discard these fields
            current_pool_uuid: _1,
            designated_pool_uuid: _2,
            ...serviceFields
        } = service;
        return getServiceCreatePrice(
            prices,
            { ...serviceFields, status: "running", price_multipliers: getSelectedImage().prices },
            false,
            /** Package price is always calculated for a single node */
            0,
        );
    }

    const onSubmit: SubmitHandler<ServiceCreateInputs> = async (inputs) => {
        // extract some props from body
        const {
            location,
            size,
            service,
            network_uuid,
            failover_nodes,
            begin,
            timeZone,
            machineImageVersion,
            kubernetesVersion,
            node_pools,
            ...body
        } = inputs;

        let packageParameters: ManagedServiceCreateBody["package_parameters"];

        if (service.os_name === "kubernetes") {
            const maintenance = getFormattedMaintenance(
                begin ?? "",
                timeZone ?? "",
                !!machineImageVersion,
                !!kubernetesVersion,
            );

            packageParameters = {
                location,
                network_uuid,
                spec: {
                    maintenance,
                    provider: { workers: node_pools ?? [] },
                },
            };
        } else {
            packageParameters = {
                location,
                network_uuid,
            };
        }

        await mutation.mutateAsync({
            requestUuid,
            inputs,
            // XXX: Need to remove nulls or backend breaks due to hug issues (WAR-5843)
            body: filterObjNulls({
                ...body,
                is_multi_node: getMultiNodeSettings().available && failover_nodes > 0,
                service: service.os_name,
                version: service.os_version,
                vm_cpu: size.vcpu,
                vm_disk_gb: size.disks,
                vm_ram: size.ram,
                package_parameters: packageParameters,
            }),
        });
    };

    const multiNodeSettings = getMultiNodeSettings();
    const os = extractOsFields(watch("service"));
    const { os_name } = os;
    const createFields = {
        ...os,
        location: watch("location"),
    } satisfies VmPriceFieldsOmitSize;

    const multiNodeDescription = multiNodeSettings.description ? (
        os_name === "postgresql" ? (
            <Trans>PostgreSQL multi-node setup is a Primary/Standby setup with automatic fail-over.</Trans>
        ) : (
            multiNodeSettings.description
        )
    ) : undefined;

    return (
        <WHookForm
            form={form}
            onSubmit={onSubmit}
            loader={
                mutation.isPending && (
                    <LoadingProgress
                        name={form.getValues().display_name}
                        type="managed_service"
                        lifeTime={SERVICE_CREATE_TIMER}
                    >
                        <CreateAnother type="managed_service" />
                    </LoadingProgress>
                )
            }
        >
            <CreateHero />

            <FormField wide label={t`Service`} isRequired>
                <RadioGroupDiv>
                    <Controller
                        control={control}
                        name="service"
                        render={({ field }) => (
                            <OsVersionSelectCustom
                                items={availableImages}
                                value={field.value}
                                onChange={viewModel.onChangeService}
                            />
                        )}
                    />
                </RadioGroupDiv>
            </FormField>

            {locations.showLocations && (
                <Controller
                    control={control}
                    name="location"
                    render={({ field }) => (
                        <LocationFieldCustom
                            value={field.value}
                            onChange={viewModel.onLocationChange}
                            locations={locations}
                        />
                    )}
                />
            )}

            {!isKubernetesSelected() && (
                <FormField wide label={t`Size`} isRequired>
                    <RadioGroupDiv>
                        <Controller
                            control={control}
                            name="size"
                            render={() => (
                                <VmSizeSelect
                                    vmData={createFields}
                                    value={watch("size")}
                                    sliderRanges={watch("size_ranges")}
                                    onChange={viewModel.onSizeChange}
                                    priceCalculator={priceCalculator}
                                    sizeParams={viewModel.getSizeParams()}
                                />
                            )}
                        />
                    </RadioGroupDiv>
                </FormField>
            )}

            <Separator />

            {multiNodeSettings.available && (
                <FormField
                    isRequired
                    label={<Trans comment="The number of fail-over nodes:">Fail-over nodes</Trans>}
                    description={multiNodeDescription}
                >
                    <Controller
                        control={control}
                        name="failover_nodes"
                        render={({ field }) => (
                            <FailoverNodeSelect
                                size={watch("size")}
                                location={watch("location")}
                                serviceName={os_name}
                                isRequired
                                isDisabled={field.disabled}
                                valueKey={String(field.value)}
                                onChange={(value) => field.onChange(value.node_count)}
                            />
                        )}
                    />
                </FormField>
            )}

            {!isKubernetesSelected() && (
                <CreateAndAssignIpField<ServiceCreateInputs>
                    name="reserve_public_ip"
                    control={control}
                    location={watch("location")}
                />
            )}

            {privateNetworksEnabled && !isKubernetesSelected() && (
                <FormField
                    isRequired
                    label={t`VPC network`}
                    description={t`Choose a VPC network for your resource from already existing one or create a new private network.`}
                >
                    <Controller
                        control={control}
                        name="network_uuid"
                        render={({ field }) => (
                            <VpcNetworkSelect
                                location={watch("location")}
                                isDisabled={field.disabled}
                                valueKey={field.value}
                                onChange={(vpc) => field.onChange(vpc.uuid)}
                            />
                        )}
                    />
                </FormField>
            )}

            {isKubernetesSelected() && (
                <>
                    <FormField wide isRequired label={t`Node pools`}>
                        <NodePoolsList location={watch("location")} />
                    </FormField>

                    <Separator />

                    <MaintenanceFormContent />
                </>
            )}

            <Separator />

            <ServiceNameField isRequired />

            <Separator />

            {isKubernetesSelected() ? (
                <KubernetesMonthlyCostField
                    workers={watch("node_pools")}
                    location={watch("location")}
                    serviceName={os_name}
                    billingAccountId={watch("billing_account_id")}
                />
            ) : (
                <ServiceMonthlyCostField
                    size={watch("size")}
                    location={watch("location")}
                    serviceName={os_name}
                    reserve_public_ip={watch("reserve_public_ip")}
                    failover_nodes={watch("failover_nodes")}
                    billingAccountId={watch("billing_account_id")}
                />
            )}

            <BillingAccountField resourceType="managed_service" isRequired />

            <Separator />

            <CreateFormAction resourceType="managed_service" />
        </WHookForm>
    );
}

function ServiceMonthlyCostField({
    size,
    location,
    serviceName,
    reserve_public_ip,
    failover_nodes,
    billingAccountId,
}: {
    size: SizeValue;
    location: string;
    serviceName: string;
    reserve_public_ip: boolean;
    failover_nodes: number;
    billingAccountId?: number;
}) {
    const priceList = useSuspenseQueryAtom(pricesAtom);
    const allImages = useSuspenseQueryAtom(availableServiceImagesAtom);
    const imagePrices = allImages.find((i) => i.service_name === serviceName)!.prices;

    const price = getServiceCreatePrice(
        priceList,
        {
            ...sizeToVmPriceFields(size, true),
            location,
            price_multipliers: imagePrices,
            os_name: serviceName,
        },
        reserve_public_ip,
        failover_nodes,
    );

    return <MonthlyCostField price={price} defaultLocation={location} billingAccountId={billingAccountId} />;
}

function KubernetesMonthlyCostField({
    workers,
    location,
    serviceName,
    billingAccountId,
}: {
    workers: KubernetesProviderWorker[] | undefined;
    location: string;
    serviceName: string;
    billingAccountId?: number;
}) {
    const priceList = useSuspenseQueryAtom(pricesAtom);
    const allImages = useSuspenseQueryAtom(availableServiceImagesAtom);
    const imagePrices = allImages.find((i) => i.service_name === serviceName)!.prices;

    const service: KubernetesServicePriceFields = {
        service: "kubernetes",
        properties: {
            location,
            spec: {
                hibernation: {
                    enabled: false,
                },
                provider: {
                    type: "warren",
                    workers: workers ?? [],
                },
            },
        },
        prices: imagePrices,
    };

    const price = getKubernetesPrice(priceList, service);

    return (
        <MonthlyCostField defaultLocation={location} billingAccountId={billingAccountId}>
            <EstimatedMonthlyCostRange
                minimum={price.minimum}
                maximum={price.maximum}
                billingAccountId={billingAccountId}
            />
        </MonthlyCostField>
    );
}

function ServiceNameField(textFieldProps: WTextFieldProps) {
    const { t } = useLingui();
    const form = useFormContext<ServiceCreateInputs>();
    const { ref, props } = useAriaField("display_name", form, {
        isRequired: textFieldProps.isRequired,
    });
    return (
        <WTextField {...props} label={t`Service name`} {...textFieldProps}>
            <Input ref={ref} className={cn(FF.FormFieldInput, TF.Input)} />
        </WTextField>
    );
}

function CreateHero() {
    const { t } = useLingui();
    return (
        <HeroBlock title={t`Create new`} icon="jp-compute-cloud-icon">
            {getResourceDescription("managed_service")}
        </HeroBlock>
    );
}

/** Debugger to check if re-loading the form works (this is used to eg. retry creating a Service from the error notification) */
function ServiceCreateDebugger({ inputs, onChangeInputs, ...props }: ServiceCreateProps) {
    return (
        <FormDebugger inputs={inputs} onChangeInputs={onChangeInputs}>
            {({ key, inputs, onChangeInputs }) => (
                <ServiceCreateForm key={key} {...props} inputs={inputs} onChangeInputs={onChangeInputs} />
            )}
        </FormDebugger>
    );
}
